import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Bell, CheckCheck, AlertTriangle, FileSignature, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const STAGE_LABEL_BY_TYPE = { qa: 'QA', progress: 'Progress' };

const ICON_BY_TYPE = {
  stage_complete: (c) => <CheckCircle2 className={`h-4 w-4 ${c === 'green' ? 'text-emerald-500' : 'text-slate-400'}`} />,
  stage_blocked:  () => <XCircle className="h-4 w-4 text-red-500" />,
  issue:          () => <AlertTriangle className="h-4 w-4 text-red-500" />,
  signoff:        () => <FileSignature className="h-4 w-4 text-blue-500" />,
};
const BG_BY_TYPE = {
  stage_complete: 'bg-emerald-50 border-emerald-200',
  stage_blocked:  'bg-red-50 border-red-200',
  issue:          'bg-red-50 border-red-200',
  signoff:        'bg-blue-50 border-blue-200',
};

function getIcon(n) {
  const fn = ICON_BY_TYPE[n.type];
  return fn ? fn(n.color) : <Bell className="h-4 w-4 text-slate-400" />;
}

function getDeepLink(n) {
  const d = n.data || {};
  if (d.unit_id && d.stage_type && d.stage_number) {
    return `/units/${d.unit_id}/${d.stage_type}?stage=${d.stage_number}`;
  }
  if (d.unit_id) return `/units/${d.unit_id}`;
  return null;
}

function NotificationItem({ notification: n, onMarkRead, onNavigate }) {
  const deepLink = getDeepLink(n);
  const handleClick = () => {
    if (!n.read) onMarkRead(n.id);
    if (deepLink) onNavigate(deepLink);
  };
  const unreadBg = BG_BY_TYPE[n.type] || 'bg-blue-50 border-blue-200';
  const readBg = 'bg-white border-slate-200';
  const bg = n.read ? readBg : unreadBg;
  const d = n.data || {};
  const typeLabel = STAGE_LABEL_BY_TYPE[d.stage_type] || null;

  return (
    <div
      onClick={handleClick}
      className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${bg} hover:brightness-[0.98]`}
      data-testid={`notification-${n.id}`}
    >
      <div className="mt-0.5">{getIcon(n)}</div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${n.read ? 'text-slate-600' : 'text-slate-900 font-semibold'}`}>{n.title}</p>
        {(d.unit_name || typeLabel) && (
          <p className="text-xs text-slate-700 mt-0.5">
            {d.unit_name}
            {typeLabel ? ` · ${typeLabel} Stage ${d.stage_number}` : ''}
            {d.stage_name ? ` – ${d.stage_name}` : ''}
          </p>
        )}
        {d.signoff_name && (
          <p className="text-xs text-slate-500 mt-0.5">
            {n.type === 'stage_complete' ? 'Signed off by ' : ''}
            <span className="font-medium text-slate-700">{d.signoff_name}</span>
          </p>
        )}
        {!d.unit_name && <p className="text-xs text-slate-500 mt-0.5">{n.message}</p>}
        <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
      </div>
      {!n.read && <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 flex-shrink-0" />}
    </div>
  );
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    try {
      const r = await api.get('/notifications');
      setNotifications(r.data);
    } catch (err) {
      // Non-blocking: show toast only for non-offline errors so users see
      // a meaningful signal if the backend is genuinely failing
      if (!err?.__offline) toast.error('Unable to load notifications');
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markRead = useCallback(async (id) => {
    await api.put(`/notifications/${id}/read`);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.put('/notifications/read-all');
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const handleNavigate = useCallback((path) => { navigate(path); }, [navigate]);
  const hasUnread = notifications.some(n => !n.read);

  const renderBody = () => {
    if (loading) {
      return <div className="text-center py-12 text-slate-400" data-testid="notifications-loading">Loading...</div>;
    }
    if (notifications.length === 0) {
      return <div className="text-center py-12 text-slate-400" data-testid="notifications-empty">No notifications yet</div>;
    }
    return (
      <div className="space-y-2" data-testid="notifications-list">
        {notifications.map(n => (
          <NotificationItem key={n.id} notification={n} onMarkRead={markRead} onNavigate={handleNavigate} />
        ))}
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="notifications-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Notifications</h1>
            <p className="text-xs text-slate-500 mt-0.5">Stage GREEN/RED alerts, issues, and sign-off requests</p>
          </div>
          {hasUnread && (
            <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-blue-600" data-testid="mark-all-read-button">
              <CheckCheck className="h-3.5 w-3.5 mr-1" />Mark all read
            </Button>
          )}
        </div>
        {renderBody()}
      </div>
    </Layout>
  );
}
